import { useSelector } from 'react-redux';
import { isBrowser } from 'react-device-detect';
import { ToastContainer } from 'react-toastify';
import { Typography } from '@mui/material';
// routes
import { Navigate, Route, Routes } from 'react-router-dom';
import { routesData } from './routes';
import { SurveyGateProvider } from './contexts/SurveyGateContext';
// theme
import ThemeProvider from './theme';
// components
import ScrollToTop from './components/scroll-to-top';
import { StyledChart } from './components/chart';
import { WithAuth } from './WithAuth';
import Page404 from './pages/Page404';

// ----------------------------------------------------------------------

export default function App() {
  const accountData = useSelector((state) => state?.login?.user || null);
  const companyId = accountData?.companyId;

  // Function to recursively render routes
  const renderRoutes = (routes, parentKey = 'root', inheritedProtected = false) => {
    // Function to process each individual route
    const processRoute = (route, index) => {
      const routeKey = `${parentKey}-${route.path || 'no-path'}-${index}`;
      if (route.redirect) {
        // return navigate(route.redirect, { replace: true });
        return <Route key={routeKey} path={route.path} element={<Navigate to={route.redirect} replace />} />;
      }
      let Component = route.component;

      // `isProtected` is declared on the top-level layout route only; propagate it
      // to nested routes so every page inherits the auth/permission guard.
      const isProtected = route.isProtected ?? inheritedProtected;

      // Determine if the route should be hidden based on companyId / companyIds
      const hide =
        (route.companyId && route.companyId !== companyId) ||
        (route.companyIds && !route.companyIds.includes(companyId));
      if (hide) {
        Component = Page404;
      } else if (isProtected) {
        // Wrap protected route components with authentication + permission checks.
        Component = WithAuth(route.component, route.permission, isProtected);
      }

      // If the route has nested routes, render them recursively
      if (route.routes) {
        return (
          <Route key={routeKey} path={route.path} element={<route.layout />}>
            {renderRoutes(route.routes, routeKey, isProtected)}
          </Route>
        );
      }

      // Return the route element
      return <Route key={routeKey} path={route.path} element={<Component />} />;
    };

    // Map over the routes array and process each route
    return routes.map(processRoute);
  };

  return isBrowser ? (
    <ThemeProvider>
      <SurveyGateProvider>
        <ScrollToTop />
        <StyledChart />
        <Routes>{renderRoutes(routesData)}</Routes>
        <ToastContainer autoClose={1000} hideProgressBar newestOnTop />
      </SurveyGateProvider>
    </ThemeProvider>
  ) : (
    <Typography variant="h3"> You must use this application from Desktop/Laptop only.</Typography>
  );
}
